export type SortOption<T = unknown> = {
  id: Extract<keyof T, string> | string;
  desc: boolean;
};

type SortFieldMap<T> = Partial<Record<string, Extract<keyof T, string> | string>>;

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce((current: unknown, key: string) => {
    if (!current || typeof current !== 'object') return undefined;

    // Handle array indices like "0", "1", etc.
    if (!isNaN(Number(key))) {
      return Array.isArray(current) ? current[Number(key)] : undefined;
    }

    return key in current ? (current as Record<string, unknown>)[key] : undefined;
  }, obj);
}

function getValue<T>(item: T, key: string): unknown {
  if (key.includes('.')) {
    return getNestedValue(item, key);
  }

  const record = item as Record<string, unknown>;
  if (key === 'storeInfo' || key === 'storeName') {
    return record.storeName ?? record.storeInfo ?? record.storeNameEng;
  }
  if (key === 'prefecture' || key === 'location') {
    return record.prefecture ?? record.locationName ?? record.location;
  }

  return record[key];
}

export function sortBy<T>(items: T[], sortOptions?: SortOption<T>[], sortFieldMap?: SortFieldMap<T>): T[] {
  if (!Array.isArray(sortOptions) || sortOptions.length === 0) return items;

  const [{ id, desc }] = sortOptions;
  const actualSortKey = sortFieldMap?.[id] ?? id;

  return [...items].sort((a, b) => {
    const aValue = getValue(a, actualSortKey);
    const bValue = getValue(b, actualSortKey);

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return desc ? bValue.localeCompare(aValue, 'ja') : aValue.localeCompare(bValue, 'ja');
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return desc ? bValue - aValue : aValue - bValue;
    }

    return 0;
  });
}
